/**
 * `@mail-sv/ui` の書式関数で足りないものだけを足す。
 * 既にあるもの（formatBytes / formatCount / formatDateTime / formatRelative / formatPercent）は
 * **再実装しない**。admin と portal で桁や単位が食い違うと運用の会話が壊れる。
 */

/** 金額。請求は円建て固定なので通貨コードは引数にしない。 */
export function formatJpy(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
}

/**
 * `yyyyMM`（区切りなし）の年月を「2026年8月」にする。
 *
 * **API が返す年月は必ず区切りなし**（`lib/time.ts` の全関数がこの表記を
 * 前提にしていて、`yyyy-MM` を渡すとその場で throw する）。画面側で
 * `new Date()` に食わせて整形しようとすると、`'202608'` が
 * エポック近傍の日付として解釈されて「1970年」になるので、文字列で切る。
 */
export function formatYearMonth(yyyyMM: string | null | undefined): string {
  // `?? '—'` だと**空文字がすり抜けて空欄になる**（null は「—」なのに
  // 空文字だけセルが空になり、書式の不具合か値が無いのか見分けが付かない）。
  if (!yyyyMM) return '—';
  if (!/^[0-9]{6}$/.test(yyyyMM)) return yyyyMM;
  return `${yyyyMM.slice(0, 4)}年${Number(yyyyMM.slice(4, 6))}月`;
}

/**
 * 直近 n か月の `yyyyMM` を新しい順で返す（絞り込みの選択肢用）。
 *
 * **自由入力にしない。** `yyyy-MM` と打たれるとサーバーの zod が 400 を返す
 * （年月表記はリポジトリ全体で区切り無し）。選択肢にしておけば形が崩れない。
 * 月の計算は `Date` の日にちを 1 に固定してから引く——月末に実行したとき、
 * 31 日から 1 か月引いて「同じ月」に戻る事故を避けるため。
 */
export function recentMonthOptions(count: number): Array<{ value: string; label: string }> {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth(), 1);
  const out: Array<{ value: string; label: string }> = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
    const ym = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
    out.push({ value: ym, label: formatYearMonth(ym) });
  }
  return out;
}

/** 所要時間。ジョブ一覧の「所要」列で使う。 */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms) || ms < 0) return '—';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}秒`;
  const min = Math.floor(sec / 60);
  const restSec = sec % 60;
  if (min < 60) return `${min}分${String(restSec).padStart(2, '0')}秒`;
  const hour = Math.floor(min / 60);
  const restMin = min % 60;
  if (hour < 24) return `${hour}時間${String(restMin).padStart(2, '0')}分`;
  return `${Math.floor(hour / 24)}日${hour % 24}時間`;
}

/** 稼働時間（秒）。サービス状態の uptime 表示。 */
export function formatUptime(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || Number.isNaN(sec)) return '—';
  return formatDuration(sec * 1000);
}

/** 前月比などの増減。0 は「±0」ではなく「変化なし」と出す。 */
export function formatDelta(delta: number | null | undefined): string {
  if (delta === null || delta === undefined || Number.isNaN(delta)) return '—';
  if (delta === 0) return '変化なし';
  const sign = delta > 0 ? '+' : '−';
  return `${sign}${Math.abs(delta).toLocaleString('ja-JP')}`;
}

/** 増減の色。増加が常に良いとは限らないので、呼び出し側が good を指定する。 */
export function deltaTone(
  delta: number | null | undefined,
  increaseIsGood: boolean,
): 'success' | 'warning' | 'default' {
  if (!delta) return 'default';
  const good = delta > 0 ? increaseIsGood : !increaseIsGood;
  return good ? 'success' : 'warning';
}

/** 残日数。証明書の有効期限バッジで使う。 */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
}

/** 0 除算を避けた使用率。分母 0（無制限）のときは null。 */
export function ratioOf(used: number, total: number): number | null {
  if (!Number.isFinite(total) || total <= 0) return null;
  return used / total;
}
